import type React from 'react';
import { createContext, useContext, useState } from 'react';
import { useUserMetadata } from '../hooks/useUserMetadata';

interface UserMetadata {
  firstName: string;
  lastName: string;
  timeZone: string;
  isLocalTime: boolean;
  phoneNumber: string;
  notificationPreference: string;
  org_id?: string;
}

interface UserMetadataContextType {
  userMetadata: UserMetadata;
  isLoading: boolean;
  isUserMetadataLoaded: boolean;
  hasSelectedOrganization: boolean;
  setTimeZone: (newTimeZone: string) => void;
  setUserMetadata: (metadata: Partial<UserMetadata>) => void;
  clearSelectedOrganization: () => void;
  isOrgModalOpen: boolean;
  openOrgModal: () => void;
  closeOrgModal: () => void;
  refreshUserData: () => void;
}

const UserMetadataContext = createContext<UserMetadataContextType | undefined>(undefined);

const defaultMetadata: UserMetadata = {
  firstName: '',
  lastName: '',
  timeZone: '',
  isLocalTime: false,
  phoneNumber: '',
  notificationPreference: '',
  org_id: '',
};

export const UserMetadataProvider = ({ children }: { children: React.ReactNode }) => {
  const { userMetadata: remoteData, isLoading, refreshUserData } = useUserMetadata();
  const [localMetadata, setLocalMetadata] = useState<UserMetadata | null>(null);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);

  const currentUserMetadata = localMetadata || remoteData || defaultMetadata;

  const isUserMetadataLoaded = Boolean(remoteData && !isLoading);

  return (
    <UserMetadataContext.Provider
      value={{
        userMetadata: currentUserMetadata,
        isLoading,
        isUserMetadataLoaded,
        hasSelectedOrganization: !!currentUserMetadata.org_id,
        isOrgModalOpen,
        openOrgModal: () => setIsOrgModalOpen(true),
        closeOrgModal: () => setIsOrgModalOpen(false),
        setTimeZone: () => refreshUserData(),

        setUserMetadata: (newMetadata: Partial<UserMetadata>) => {
          setLocalMetadata((prev) => ({
            ...(prev || remoteData || defaultMetadata),
            ...newMetadata,
          }));
          refreshUserData();
        },

        clearSelectedOrganization: () => {},
        refreshUserData: refreshUserData,
      }}
    >
      {children}
    </UserMetadataContext.Provider>
  );
};

export const useUserMetadataContext = () => {
  const context = useContext(UserMetadataContext);
  if (!context) {
    throw new Error('useUserMetadataContext must be used within a UserMetadataProvider');
  }
  return context;
};
